import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PublicLayout from '../../layouts/PublicLayout';
import publicBookingApi from '../../services/publicBookingApi';
import { formatDate } from '../../utils/dateFormatter';
import '../../styles/CheckoutPage.css';

/**
 * CheckoutPage - Guest information and payment
 * Route: /book/:propertyId/checkout
 *
 * Phase 1: Manual payment (bank transfer instructions)
 * Phase 2: Stripe/Hyperpay integration (payment method selection)
 */
const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Booking details passed from BookingPage
  const {
    propertyId,
    property,
    checkIn,
    checkOut,
    guests,
    pricing
  } = location.state || {};

  // Form state
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    email: '',
    phone: '+966'
  });

  const [consent, setConsent] = useState({
    terms: false,
    privacyPolicy: false,
    marketing: false
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Validation
  const [errors, setErrors] = useState({});

  // Redirect if no booking data
  useEffect(() => {
    if (!propertyId || !property || !checkIn || !checkOut || !pricing) {
      navigate(`/book/${propertyId || ''}`);
    }
  }, [propertyId, property, checkIn, checkOut, pricing, navigate]);

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!guestInfo.name.trim()) {
      newErrors.name = 'الاسم مطلوب';
    } else if (guestInfo.name.trim().length < 2) {
      newErrors.name = 'الاسم يجب أن يكون حرفين على الأقل';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!guestInfo.email.trim()) {
      newErrors.email = 'البريد الإلكتروني مطلوب';
    } else if (!emailRegex.test(guestInfo.email)) {
      newErrors.email = 'البريد الإلكتروني غير صحيح';
    }

    // Phone validation (Saudi format)
    const phoneRegex = /^\+966[0-9]{9}$/;
    if (!guestInfo.phone.trim()) {
      newErrors.phone = 'رقم الجوال مطلوب';
    } else if (!phoneRegex.test(guestInfo.phone)) {
      newErrors.phone = 'رقم الجوال يجب أن يكون بصيغة: +966501234567';
    }

    // Consent validation
    if (!consent.terms) {
      newErrors.terms = 'يجب الموافقة على الشروط والأحكام';
    }
    if (!consent.privacyPolicy) {
      newErrors.privacyPolicy = 'يجب الموافقة على سياسة الخصوصية';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Create booking
      const response = await publicBookingApi.createBooking({
        propertyId,
        checkIn,
        checkOut,
        guests,
        guestInfo: {
          name: guestInfo.name.trim(),
          email: guestInfo.email.trim().toLowerCase(),
          phone: guestInfo.phone.trim()
        },
        bookingOrigin: {
          source: 'direct-link',
          referrer: document.referrer || 'direct'
        },
        consent
      }, 'ar');

      if (response.success) {
        // Navigate to confirmation page
        navigate(`/booking/confirmation/${response.booking.confirmationCode}`, {
          replace: true
        });
      } else {
        setError(response.error || 'فشل في إنشاء الحجز. يرجى المحاولة مرة أخرى.');
      }
    } catch (err) {
      console.error('Error creating booking:', err);
      setError('حدث خطأ في إنشاء الحجز. يرجى المحاولة مرة أخرى.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (!property) {
    return (
      <PublicLayout>
        <div className="checkout-loading">
          <div className="spinner"></div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="checkout-page" dir="rtl">
        <div className="checkout-container">
          {/* Progress Indicator */}
          <div className="checkout-progress">
            <div className="progress-step completed">
              <div className="step-number">1</div>
              <div className="step-label">اختيار التواريخ</div>
            </div>
            <div className="progress-line completed"></div>
            <div className="progress-step active">
              <div className="step-number">2</div>
              <div className="step-label">معلومات الضيف</div>
            </div>
            <div className="progress-line"></div>
            <div className="progress-step">
              <div className="step-number">3</div>
              <div className="step-label">التأكيد</div>
            </div>
          </div>

          <div className="checkout-content">
            {/* Left Side - Form */}
            <div className="checkout-form-section">
              <h1 className="checkout-title">إكمال الحجز</h1>

              {error && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle"></i>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Guest Information */}
                <div className="form-section">
                  <h2 className="section-title">معلومات الضيف</h2>

                  <div className="form-group">
                    <label htmlFor="name">الاسم الكامل *</label>
                    <input
                      type="text"
                      id="name"
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      value={guestInfo.name}
                      onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value })}
                      placeholder="أدخل اسمك الكامل"
                      disabled={submitting}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">البريد الإلكتروني *</label>
                    <input
                      type="email"
                      id="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      value={guestInfo.email}
                      onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })}
                      placeholder="example@email.com"
                      disabled={submitting}
                    />
                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    <small className="form-text">سيتم إرسال تأكيد الحجز إلى هذا البريد</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">رقم الجوال *</label>
                    <input
                      type="tel"
                      id="phone"
                      className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                      value={guestInfo.phone}
                      onChange={(e) => setGuestInfo({ ...guestInfo, phone: e.target.value })}
                      placeholder="+966501234567"
                      disabled={submitting}
                    />
                    {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                    <small className="form-text">مثال: +966501234567</small>
                  </div>
                </div>

                {/* Payment Method Section - Structured for Phase 2 Integration */}
                <div className="form-section payment-section">
                  <h2 className="section-title">طريقة الدفع</h2>

                  {/* Phase 1: Manual Payment Notice */}
                  <div className="payment-method-notice">
                    <div className="payment-icon">
                      <i className="bi bi-bank"></i>
                    </div>
                    <div className="payment-info">
                      <h3>تحويل بنكي</h3>
                      <p>سيتم إرسال تعليمات الدفع إلى بريدك الإلكتروني بعد تأكيد الحجز</p>
                    </div>
                  </div>

                  {/*
                    Phase 2: Replace above with payment method selection

                    <div className="payment-methods">
                      <div className="payment-method-option">
                        <input type="radio" name="paymentMethod" value="card" />
                        <label>بطاقة ائتمانية</label>
                      </div>
                      <div className="payment-method-option">
                        <input type="radio" name="paymentMethod" value="bank" />
                        <label>تحويل بنكي</label>
                      </div>
                    </div>

                    {paymentMethod === 'card' && (
                      <div className="stripe-elements-container">
                        <StripeCardElement />
                      </div>
                    )}
                  */}
                </div>

                {/* Terms and Consent */}
                <div className="form-section consent-section">
                  <h2 className="section-title">الشروط والموافقات</h2>

                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="terms"
                      className={`form-check-input ${errors.terms ? 'is-invalid' : ''}`}
                      checked={consent.terms}
                      onChange={(e) => setConsent({ ...consent, terms: e.target.checked })}
                      disabled={submitting}
                    />
                    <label htmlFor="terms" className="form-check-label">
                      أوافق على <a href="/terms" target="_blank">الشروط والأحكام</a> *
                    </label>
                    {errors.terms && <div className="invalid-feedback d-block">{errors.terms}</div>}
                  </div>

                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="privacy"
                      className={`form-check-input ${errors.privacyPolicy ? 'is-invalid' : ''}`}
                      checked={consent.privacyPolicy}
                      onChange={(e) => setConsent({ ...consent, privacyPolicy: e.target.checked })}
                      disabled={submitting}
                    />
                    <label htmlFor="privacy" className="form-check-label">
                      أوافق على <a href="/privacy" target="_blank">سياسة الخصوصية</a> *
                    </label>
                    {errors.privacyPolicy && <div className="invalid-feedback d-block">{errors.privacyPolicy}</div>}
                  </div>

                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="marketing"
                      className="form-check-input"
                      checked={consent.marketing}
                      onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })}
                      disabled={submitting}
                    />
                    <label htmlFor="marketing" className="form-check-label">
                      أرغب في تلقي العروض والتحديثات (اختياري)
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="btn-submit-booking"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" style={{ marginLeft: '8px' }}></span>
                      جاري إنشاء الحجز...
                    </>
                  ) : (
                    'تأكيد الحجز'
                  )}
                </button>

                <p className="secure-notice">
                  <i className="bi bi-shield-check"></i>
                  معلوماتك آمنة ومحمية
                </p>
              </form>
            </div>

            {/* Right Side - Booking Summary */}
            <div className="checkout-summary-section">
              <div className="booking-summary-card">
                <h2 className="summary-title">ملخص الحجز</h2>

                {/* Property Info */}
                <div className="summary-property">
                  <img
                    src={property.bookingPageSettings?.primaryPhoto || property.photos?.[0] || '/placeholder.jpg'}
                    alt={property.name}
                    className="summary-property-image"
                  />
                  <h3 className="summary-property-name">{property.name}</h3>
                  <p className="summary-property-location">
                    <i className="bi bi-geo-alt"></i>
                    {property.city || property.address}
                  </p>
                </div>

                <hr />

                {/* Booking Details */}
                <div className="summary-details">
                  <div className="summary-row">
                    <span className="summary-label">
                      <i className="bi bi-calendar-check"></i>
                      تسجيل الوصول
                    </span>
                    <span className="summary-value">{formatDate(checkIn)}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">
                      <i className="bi bi-calendar-x"></i>
                      تسجيل المغادرة
                    </span>
                    <span className="summary-value">{formatDate(checkOut)}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">
                      <i className="bi bi-people"></i>
                      عدد الضيوف
                    </span>
                    <span className="summary-value">{guests} {guests === 1 ? 'ضيف' : 'ضيوف'}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">
                      <i className="bi bi-moon"></i>
                      عدد الليالي
                    </span>
                    <span className="summary-value">{pricing.nights} {pricing.nights === 1 ? 'ليلة' : 'ليالي'}</span>
                  </div>
                </div>

                <hr />

                {/* Price Breakdown */}
                <div className="summary-pricing">
                  <div className="summary-row">
                    <span>الإجمالي الفرعي</span>
                    <span>{pricing.subtotal} ريال</span>
                  </div>
                  <div className="summary-row">
                    <span>الضريبة (15%)</span>
                    <span>{pricing.tax} ريال</span>
                  </div>
                  <hr className="summary-divider" />
                  <div className="summary-row summary-total">
                    <span>الإجمالي</span>
                    <span>{pricing.total} ريال</span>
                  </div>
                </div>

                {/* Cancellation Policy */}
                <div className="summary-policy">
                  <p className="policy-text">
                    <i className="bi bi-info-circle"></i>
                    {property.bookingPageSettings?.cancellationPolicy?.type === 'flexible' &&
                      'سياسة إلغاء مرنة - استرداد كامل حتى 7 أيام قبل الوصول'}
                    {property.bookingPageSettings?.cancellationPolicy?.type === 'moderate' &&
                      'سياسة إلغاء معتدلة - استرداد كامل حتى 14 يوم قبل الوصول'}
                    {property.bookingPageSettings?.cancellationPolicy?.type === 'strict' &&
                      'سياسة إلغاء صارمة - استرداد 50% حتى 30 يوم قبل الوصول'}
                    {!property.bookingPageSettings?.cancellationPolicy &&
                      'سياسة إلغاء معتدلة - استرداد كامل حتى 14 يوم قبل الوصول'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default CheckoutPage;
